import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ThumbsUp, ThumbsDown, CheckCircle, XCircle, Loader2, ArrowLeftRight } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  Climate: "#10B981", Housing: "#3B82F6", Economy: "#F59E0B",
  Indigenous: "#DC2626", Disability: "#8B5CF6", Environment: "#059669",
  Integrity: "#6366F1", Defence: "#1E40AF", Education: "#0EA5E9",
  "Social Policy": "#EC4899", "Gambling Reform": "#D97706",
  "Foreign Policy": "#64748B", Energy: "#EF4444", Immigration: "#7C3AED",
  Agriculture: "#065F46", "Digital Safety": "#0891B2", "Human Rights": "#BE185D",
  default: "#6B7280",
};

export default function PolicyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/policies/${id}`)
      .then((r) => setPolicy(r.data))
      .catch(() => setPolicy(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: bg }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#FFC82E" }} />
      </div>
    );
  }

  if (!policy) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ backgroundColor: bg }}>
        <p className="font-black text-lg" style={{ color: text }}>Policy not found</p>
        <button onClick={() => navigate("/policies")} className="px-4 py-2 border-2 border-black font-bold text-sm" style={{ backgroundColor: "#FFC82E", color: "#000" }}>
          Back to Policies
        </button>
      </div>
    );
  }

  const catColor = CATEGORY_COLORS[policy.category] || CATEGORY_COLORS.default;
  const ayePct = policy.aye_count + policy.no_count > 0
    ? Math.round((policy.aye_count / (policy.aye_count + policy.no_count)) * 100) : 0;

  const filteredVotes = filter === "all" ? policy.votes
    : policy.votes.filter((v) => v.vote === filter);

  // Group by party for summary
  const byParty = {};
  policy.votes.forEach((v) => {
    if (!byParty[v.party_name]) byParty[v.party_name] = { color: v.party_color, aye: 0, no: 0 };
    byParty[v.party_name][v.vote === "aye" ? "aye" : "no"]++;
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="policy-detail-page">
      {/* Hero */}
      <div className="border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="h-2" style={{ backgroundColor: catColor }} />
        <div className="max-w-5xl mx-auto px-4 py-8">
          <button
            onClick={() => navigate("/policies")}
            className="flex items-center gap-1.5 text-sm font-bold mb-5 hover:opacity-75 transition-opacity"
            style={{ color: "#FFC82E" }}
            data-testid="back-policies-btn"
          >
            <ArrowLeft className="w-4 h-4" /> All Policy Votes
          </button>

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-xs font-bold px-2 py-0.5 border" style={{ color: catColor, borderColor: catColor }}>
              {policy.category}
            </span>
            <span className="text-xs" style={{ color: "#64748B" }}>{policy.date}</span>
            <div className="flex items-center gap-1 text-xs font-bold" style={{ color: policy.passed ? "#10B981" : "#EF4444" }}>
              {policy.passed ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
              {policy.passed ? "Passed" : "Did not pass"}
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-white mb-4" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="policy-title">
            {policy.bill}
          </h1>

          {/* Stats */}
          <div className="flex flex-wrap gap-3">
            {[
              { label: "Total Votes Recorded", value: policy.votes.length, color: "#FFC82E" },
              { label: "Voted Aye", value: policy.aye_count, color: "#10B981" },
              { label: "Voted No", value: policy.no_count, color: "#EF4444" },
              { label: "Aye Rate", value: `${ayePct}%`, color: "#38BDF8" },
            ].map(({ label, value, color }) => (
              <div key={label} className="border-2 border-black px-4 py-2" style={{ backgroundColor: color + "15" }} data-testid={`policy-stat-${label.toLowerCase().replace(/ /g, "-")}`}>
                <span className="text-xl font-black" style={{ color }}>{value}</span>
                <span className="text-xs font-bold ml-2 text-white">{label}</span>
              </div>
            ))}
          </div>

          {/* Visual bar */}
          <div className="mt-4 h-3 border-2 border-white overflow-hidden flex max-w-md">
            <div className="h-full" style={{ width: `${ayePct}%`, backgroundColor: "#10B981" }} />
            <div className="h-full flex-1" style={{ backgroundColor: "#EF4444" }} />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        {/* Party breakdown */}
        {Object.keys(byParty).length > 0 && (
          <section data-testid="party-breakdown">
            <h2 className="text-xl font-black mb-4 pb-2 border-b-2 border-black" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
              Party Breakdown
            </h2>
            <div className="flex flex-wrap gap-3">
              {Object.entries(byParty).map(([party, counts]) => (
                <div key={party} className="border-2 border-black px-4 py-2.5 flex items-center gap-3" style={{ backgroundColor: surface, borderLeftColor: counts.color, borderLeftWidth: 4 }}>
                  <span className="font-black text-sm" style={{ color: text }}>{party}</span>
                  {counts.aye > 0 && <span className="text-xs font-black px-1.5 py-0.5 border border-green-500 text-green-500">{counts.aye} Aye</span>}
                  {counts.no > 0 && <span className="text-xs font-black px-1.5 py-0.5 border border-red-500 text-red-500">{counts.no} No</span>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Votes list */}
        <section data-testid="votes-list-section">
          <div className="flex items-center justify-between mb-4 pb-2 border-b-2 border-black">
            <h2 className="text-xl font-black" style={{ color: text, fontFamily: "Outfit, sans-serif" }}>
              How They Voted
              <span className="text-sm font-bold ml-2" style={{ color: textSec }}>
                ({filteredVotes.length} of {policy.votes.length})
              </span>
            </h2>
            <div className="flex gap-1.5">
              {["all", "aye", "no"].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  data-testid={`vote-filter-${f}`}
                  className="px-3 py-1 text-xs font-black border-2 border-black capitalize"
                  style={{
                    backgroundColor: filter === f ? (f === "aye" ? "#10B981" : f === "no" ? "#EF4444" : "#FFC82E") : surface,
                    color: filter === f ? (f === "all" ? "#000" : "#fff") : text,
                    boxShadow: filter === f ? "2px 2px 0 #000" : "none",
                  }}
                >
                  {f === "all" ? "All" : f === "aye" ? "Aye" : "No"}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {filteredVotes.map((v) => (
              <div
                key={v.politician_id}
                className="border-2 border-black p-3"
                style={{
                  backgroundColor: v.vote === "aye" ? (isDark ? "#0D2418" : "#F0FDF4") : (isDark ? "#1F0A0A" : "#FEF2F2"),
                  boxShadow: "2px 2px 0 #000",
                }}
                data-testid={`policy-vote-${v.politician_id}`}
              >
                <div className="flex items-start gap-2.5">
                  {v.vote === "aye" ? (
                    <div className="flex-shrink-0 flex items-center gap-1 px-1.5 py-0.5 border-2 border-black text-xs font-black text-white" style={{ backgroundColor: "#10B981" }}>
                      <ThumbsUp className="w-3 h-3" /> AYE
                    </div>
                  ) : (
                    <div className="flex-shrink-0 flex items-center gap-1 px-1.5 py-0.5 border-2 border-black text-xs font-black text-white" style={{ backgroundColor: "#EF4444" }}>
                      <ThumbsDown className="w-3 h-3" /> NO
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <button
                      onClick={() => navigate(`/politicians/${v.politician_id}`)}
                      className="font-black text-sm hover:underline text-left"
                      style={{ color: text }}
                    >
                      {v.politician_name}
                    </button>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-xs font-black px-1.5 py-0.5 border text-white" style={{ backgroundColor: v.party_color, borderColor: v.party_color }}>
                        {v.party_name}
                      </span>
                      <span className="text-xs" style={{ color: textSec }}>
                        {v.chamber === "House of Representatives" ? "House" : "Senate"} · {v.state}
                      </span>
                    </div>
                    {v.description && (
                      <p className="text-xs mt-1 leading-relaxed" style={{ color: textSec }}>{v.description}</p>
                    )}
                  </div>
                  <button
                    onClick={() => navigate(`/politicians/compare?p1=${v.politician_id}`)}
                    className="flex-shrink-0 p-1.5 border border-current hover:opacity-75 transition-opacity"
                    style={{ color: textSec }}
                    title="Compare with another politician"
                    data-testid={`compare-from-policy-${v.politician_id}`}
                  >
                    <ArrowLeftRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
